import React, { useMemo } from 'react';
import { InputAdornment, Grid, TextField, IconButton, Tooltip } from '@material-ui/core';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { css } from '@emotion/react';
import { Body1 } from './blocks';
import { isFinite } from 'lodash';
import { colors } from '@/styles';
import { Autocomplete } from '@material-ui/lab';
import { COMMENT_REASON_CODES } from './constants';

const Charge = ({ charge, options, editable, callbacks, qbo_tags, isBaseCharge, isEdited }) => {
    // Every catalog service shares type SERVICE_RATE, so match on code first or they all read the
    // first service's option.
    const meta = useMemo(() => {
        const sameType = (option) => option.type === charge.type;
        if (!charge.code) return options?.find(sameType);
        return options?.find((option) => sameType(option) && option.code === charge.code) || options?.find(sameType);
    }, [charge, options]);

    const mod = isBaseCharge ? 1 : (meta?.isPercentage ? 100 : 1);

    const commentDisplay = charge.comment || '';
    const createdByDisplay = charge.created_by || '';
    const lastEditedByDisplay = charge.last_edited_by || '';
    const isAutoApplied = charge.is_auto_applied === true;
    const showCommentError = isEdited && !isAutoApplied && !commentDisplay;
    const auditTooltip = [
        createdByDisplay && `Created by: ${createdByDisplay}`,
        charge.created_at && `Created: ${new Date(charge.created_at).toLocaleString()}`,
        lastEditedByDisplay && `Last edited by: ${lastEditedByDisplay}`,
        charge.last_edited_at && `Last edited: ${new Date(charge.last_edited_at).toLocaleString()}`,
    ]
        .filter(Boolean)
        .join('\n');

    return (
        <Grid container direction="row" css={css`border-bottom: 1px solid lightgrey; padding: 12px 5px; flex-wrap: nowrap;`}>
            <Grid container direction="column" css={css`flex: 0 0 36px; max-width: 36px; margin-right: 12px; justify-content: center;`}>
                {!isBaseCharge && (
                    <IconButton disabled={!editable} onClick={() => callbacks.onRemove()}>
                        <DeleteForeverIcon css={css`color: ${colors.reds.cancelled};`}/>
                    </IconButton>
                )}
            </Grid>
            <Grid container direction="column" css={css`flex: 2; flex-basis: 0; margin-right: 12px; justify-content: center;`}>
                <Body1>{charge.label || (isBaseCharge ? 'Base Charge' : 'Custom')}</Body1>
            </Grid>
            <Grid container direction="column" css={css`flex: 2; flex-basis: 0; margin: 0 12px; justify-content: center;`}>
                <TextField fullWidth variant="outlined" type="number" size="small" disabled={!editable} value={charge.rate * mod}
                    onChange={(e) => {
                        if (isBaseCharge) {
                            let next = parseFloat(e.target.value);
                            if (isNaN(next)) {
                                next = 0;
                            }
                            callbacks.onChange(next);
                        } else {
                            let next = parseFloat(e.target.value) / mod;
                            if (isNaN(next)) {
                                next = '';
                            }
                            callbacks.onChange({...charge, rate: next, total: next * charge.quantity,});
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                {isBaseCharge ? '$' : (meta?.adornment || '$')}
                            </InputAdornment>
                        ),
                        inputProps: {
                            style: { textAlign: 'center', paddingRight: '16px' },
                        },
                    }}
                    css={css`& .MuiFormHelperText-contained { white-space: nowrap;}`}
                />
            </Grid>
            <Grid container direction="column"
                css={css`flex: 1; flex-basis: 0; margin: 0 12px;
                    ${isBaseCharge ? 'justify-content: center; align-items: center;' : ''}
                `}
            >
                {isBaseCharge ? (
                    <Body1>{'--'}</Body1>
                ) : (
                    <TextField fullWidth variant="outlined" type="number" size="small" disabled={!editable} value={charge.quantity || null}
                        onChange={(e) => {
                            const qty = e.target.value;
                            callbacks.onChange({...charge, quantity: qty, total: charge.rate * qty,});
                        }}
                        InputProps={{
                            inputProps: {style: { textAlign: 'center' },},
                        }}
                    />
                )}
            </Grid>
            <Grid container direction="column" css={css`flex: 1; flex-basis: 0; margin: 0 12px; ${isBaseCharge ? 'justify-content: center; align-items: center;' : ''}`}>
                {isBaseCharge ? (
                    <Body1>{charge.qbo_tag || '--'}</Body1>
                ) : (
                    <Autocomplete freeSolo size="small" disabled={!editable} options={qbo_tags || []} value={charge.qbo_tag || ''}
                        onChange={(_, value) => {
                            callbacks.onChange({...charge, qbo_tag: value || '',});
                        }}
                        renderInput={(params) => <TextField {...params} size="small" variant="outlined" />}
                    />
                )}
            </Grid>
            <Grid container direction="column" css={css`flex: 2; flex-basis: 0; margin: 0 12px;`}>
                <Autocomplete freeSolo size="small" disabled={!editable || isAutoApplied} options={COMMENT_REASON_CODES} value={commentDisplay}
                    onInputChange={(_, value) => {
                        if (isBaseCharge) {
                            callbacks.onCommentChange(value || '');
                        } else {
                            callbacks.onChange({...charge,comment: value || '',});
                        }
                    }}
                    renderInput={(params) => (
                        <Tooltip title={auditTooltip} placement="top" arrow>
                            <TextField {...params} size="small" variant="outlined" placeholder={isAutoApplied ? 'Auto-applied' : 'Select or type reason...'}
                                error={showCommentError} helperText={showCommentError ? 'Required' : createdByDisplay || ''}
                            />
                        </Tooltip>
                    )}
                />
            </Grid>
            <Grid container direction="column" css={css` flex: 1; flex-basis: 0; margin-left: 12px; justify-content: center; align-items: flex-end;`}>
                <Body1>{isFinite(charge.total) ? charge.total.toFixed(2) : '--'}</Body1>
            </Grid>
        </Grid>
    );
};

export default Charge;
